import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';

import { Carousel } from '../../internal/Carousel/Carousel';
import { CarouselItem } from '../../internal/Carousel/CarouselItem';
import { CarouselProductCard } from '../Cards/CarouselProductCard/components/CarouselProductCard';
import { ProductCarouselSkeleton } from '../ProductCarouselSkeleton/ProductCarouselSkeleton';
import type { ProductCarouselProps } from './types';
import { useGridColumnGap } from 'client-ribbons-design-system/hooks/useGridColumnGap';

export const PRODUCT_CAROUSEL_TEST_ID = 'product-carousel';
export const ProductCarousel = ({
  currency,
  enableFreeDomesticDeliveryLabels,
  hideLabelIcons,
  lazyImages,
  onNavigation,
  onProductClick,
  onVisible = () => {},
  products,
  ...carouselProps
}: ProductCarouselProps) => {
  const columnGap = useGridColumnGap();
  const observedElementRef = useImpressionRef<HTMLDivElement>(() => onVisible());
  if (products === null) {
    return <ProductCarouselSkeleton />;
  }
  if (products.length > 0) {
    return (
      <div data-testid={PRODUCT_CAROUSEL_TEST_ID} ref={observedElementRef}>
        <Carousel
          columnGap={columnGap}
          onNavigateLeft={({ swiped }) => onNavigation?.(swiped, 'prev')}
          onNavigateRight={({ swiped }) => onNavigation?.(swiped, 'next')}
          titleSize="regular"
          {...carouselProps}
        >
          {products.map((product, index) => (
            <CarouselItem
              ariaLabel={`slide ${index + 1} of ${products.length}`}
              key={product.title}
            >
              <CarouselProductCard
                alt={product.alt}
                currency={currency}
                enableFreeDomesticDeliveryLabels={enableFreeDomesticDeliveryLabels}
                freeDomesticDelivery={product.freeDomesticDelivery}
                hideLabelIcons={hideLabelIcons}
                href={product.href}
                imageLoading={lazyImages ? 'lazy' : 'eager'}
                isNew={product.isNew ?? false}
                linkDataAttributes={
                  product.trackingURL ? { 'tracking-url': product.trackingURL } : {}
                }
                onLinkClick={(e) => {
                  onProductClick?.(e, { index });
                }}
                preSalePrice={product.preSalePrice}
                price={product.price}
                purchasable={product.isPurchasable ?? false}
                salePercentage={product.salePercentage ?? undefined}
                src={product.src}
                title={product.title}
              />
            </CarouselItem>
          ))}
        </Carousel>
      </div>
    );
  }
  return null;
};
